import { useState, useEffect, MouseEvent } from 'react'
import {
    DownloadOutlined,
    ShareAltOutlined,
    CloseOutlined,
} from '@ant-design/icons'
import { Tabs, Tag, Tooltip } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    HEADER_FILENAME_MAX_LENGTH,
    DFM_FEEDBACK_KEY,
    PART_VIEWER_PLACEMENT,
    THREADS_KEY,
} from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/constants'
import DownloadRevisionLink from '@Builder/QuoteDetailPage/FilesTab/TableCells/AdditionalActionsCell/DownloadRevisionLink'
import ShareQuoteModal from '@Quotes/components/QuoteShare/ShareQuoteModal'
import { getUrl } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/logic'
import styles from '@Builder/QuoteDetailPage/FilesTab/styles.css'
import { shipmentLines_shipment_shipmentLines_items as ItemsType } from '@Orders/OrderDetailsPage/Queries/__generated__/shipmentLines'
import { salesOrderLines_salesOrderLines_items_quoteModelRevision_quoteModel_quote as QuoteType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrderLines'

import Box from '~/core/Box'
import Text from '~/core/Text'
import Button from '~/core/Button'
import FileName from '~/core/FileName'
import getColor from '~/lib/getColor'
import formatRevisionVersion from '~/lib/formatRevisionVersion'
import {
    PART_DETAILS_PAGES,
    INSPECTION_KEY,
    TRACK_SHIPMENT_KEY,
    SUMMARY_KEY,
} from '../logic'

export type HTMLMouseEventType =
    | ((event: MouseEvent<HTMLElement>) => void)
    | undefined

type PartViewerModalHeaderPropsType = {
    revision: ItemsType['revision'] | null
    quote?: QuoteType
    onClose: HTMLMouseEventType
    activePage: string
    tapsCount: number | null
    signedBy?: string | null
    setActivePage: (page: string) => void
    inspectionDocumentsLength: number
    dfmReportsLength?: number
    isImOrder?: boolean
    dfmFeedback: ItemsType['dfmFeedback']
    deliveredQuantity?: number | null
    showTrackOrderTab?: boolean | null
    isHighTouch?: boolean
}

export const PartViewerModalHeader = (
    props: PartViewerModalHeaderPropsType,
) => {
    const {
        revision,
        quote,
        onClose,
        inspectionDocumentsLength,
        dfmReportsLength,
        isImOrder,
        activePage,
        setActivePage,
        dfmFeedback,
        tapsCount,
        showTrackOrderTab,
        isHighTouch = false,
    } = props

    const navigate = useNavigate()
    const { pathname } = useLocation()
    const [selectedTab, setSelectedTab] = useState('')
    const [showShareModal, setShowShareModel] = useState(false)

    useEffect(() => {
        setSelectedTab(activePage)
    }, [setSelectedTab, activePage])

    let shouldShowMfgFeedbackTab =
        Boolean(dfmReportsLength) ||
        (!isImOrder && dfmFeedback && dfmFeedback?.length > 0)

    if (isHighTouch) {
        shouldShowMfgFeedbackTab = false
    }

    const downloadUri = revision?.downloadUri

    const onTabClick = (nextTab: string) => {
        if (selectedTab !== nextTab) {
            setSelectedTab(nextTab)
            setActivePage(nextTab)
            const url = getUrl(pathname, selectedTab)

            if (quote !== undefined) {
                if (nextTab === PART_DETAILS_PAGES.MANUFACTURING_FEEDBACK) {
                    navigate({
                        pathname: `${url}/${DFM_FEEDBACK_KEY}`,
                    })
                } else {
                    navigate({
                        pathname: `${url}/${nextTab}`,
                    })
                }
            }

            return null
        }
    }

    const handleModelShareModal = (show: boolean) => {
        setShowShareModel(show)
    }

    return (
        <>
            <Box padding="16px 0 0">
                <Box width="78%" alignContent="start" whiteSpace="nowrap">
                    <Box width="fit-content" paddingLeft="16px">
                        <Tag
                            style={{
                                marginTop: '4px',
                                height: 'fit-content',
                            }}
                        >
                            {formatRevisionVersion(revision?.version)}
                        </Tag>
                        <FileName
                            name={revision?.modelFileName}
                            style={{
                                fontSize: '20px',
                                fontWeight: '500',
                                color: getColor('blackAlpha65'),
                            }}
                            maxLength={HEADER_FILENAME_MAX_LENGTH}
                        />
                    </Box>
                </Box>
                <Box justifyContent="end" width="22%">
                    <Box padding="0px 8px" className={styles.hoverLink}>
                        <Tooltip title="Share model">
                            <ShareAltOutlined
                                onClick={() => {
                                    handleModelShareModal(true)
                                }}
                                style={{
                                    fontSize: '18px',
                                    marginTop: '5px',
                                }}
                                data-test-target="share-partViewer-modal"
                            />
                        </Tooltip>
                    </Box>
                    <DownloadRevisionLink downloadUrl={downloadUri}>
                        <Box padding="0px 15px" className={styles.hoverLink}>
                            <Tooltip title="Download model">
                                <DownloadOutlined
                                    style={{
                                        fontSize: '18px',
                                        marginTop: '5px',
                                    }}
                                    data-test-target="download-partViewer-modal"
                                />
                            </Tooltip>
                        </Box>
                    </DownloadRevisionLink>
                    {showShareModal && (
                        <ShareQuoteModal
                            quoteId={quote?.id}
                            quoteName={quote?.name}
                            placement={PART_VIEWER_PLACEMENT}
                            visible={showShareModal}
                            onCancel={() => handleModelShareModal(false)}
                        />
                    )}
                </Box>
                <Box
                    borderLeft={`1px solid ${getColor('blackAlpha09')}`}
                    padding="0px 15px"
                >
                    <Tooltip title="Close (Esc)">
                        <Button
                            icon={<CloseOutlined />}
                            style={{
                                fontSize: '18px',
                                border: '0px',
                                marginLeft: '-8px',
                            }}
                            placement={PART_VIEWER_PLACEMENT}
                            onClick={onClose}
                            data-test-target="close-partViewer-modal"
                            name="close-partViewer-modal"
                        />
                    </Tooltip>
                </Box>
            </Box>
            <Tabs
                className={styles.partViewerModalTabs}
                activeKey={selectedTab}
                onTabClick={onTabClick}
            >
                <Tabs.TabPane
                    tab={
                        <Text
                            data-test-target="summary-tab"
                            className={styles.tabsText}
                        >
                            {PART_DETAILS_PAGES.SUMMARY}
                        </Text>
                    }
                    key={SUMMARY_KEY}
                    data-test-target="summary-tab"
                />
                {Boolean(tapsCount) && (
                    <Tabs.TabPane
                        tab={
                            <Text className={styles.tabsText}>
                                {PART_DETAILS_PAGES.THREADS}
                            </Text>
                        }
                        key={THREADS_KEY}
                        data-test-target="threads-tab"
                    />
                )}
                {shouldShowMfgFeedbackTab && (
                    <Tabs.TabPane
                        data-test-target="manufacturability-feedback-tab"
                        tab={
                            <Text className={styles.tabsText}>
                                {PART_DETAILS_PAGES.MANUFACTURING_FEEDBACK}
                            </Text>
                        }
                        key={DFM_FEEDBACK_KEY}
                    />
                )}
                {Boolean(inspectionDocumentsLength) && (
                    <Tabs.TabPane
                        data-test-target="inspection-report-tab"
                        tab={
                            <Text className={styles.tabsText}>
                                {PART_DETAILS_PAGES.INSPECTION_REPORT}
                            </Text>
                        }
                        key={INSPECTION_KEY}
                    />
                )}
                {showTrackOrderTab && (
                    <Tabs.TabPane
                        data-test-target="track-shipment-tab"
                        tab={
                            <Text className={styles.tabsText}>
                                {PART_DETAILS_PAGES.TRACK_SHIPMENT}
                            </Text>
                        }
                        key={TRACK_SHIPMENT_KEY}
                    />
                )}
            </Tabs>
        </>
    )
}
